import { useCallback, useEffect, useState } from "react";
import PageHero from "@/components/PageHero";
import api from "@/lib/api";
import { INDIAN_STATES } from "@/lib/brand";
import { Search, MapPin, User2 } from "lucide-react";

export default function DirectoryPage({ role, eyebrow, title, subtitle }) {
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/directory/${role}`, { params: { q: q || undefined, state: state || undefined, limit: 60 } });
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  }, [role, q, state]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid={`directory-${role}`}>
      <PageHero eyebrow={eyebrow} title={title} subtitle={subtitle} />
      <section className="section-spacing nkf-container">
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder={`Search by name…`} className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md text-sm" />
          </div>
          <select data-testid="directory-state-filter" value={state} onChange={(e) => setState(e.target.value)} className="px-3 py-2.5 border border-slate-300 rounded-md text-sm md:w-60">
            <option value="">All States</option>
            {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={load} data-testid="directory-search-btn" className="bg-nkf-blue text-white uppercase tracking-widest text-xs font-bold px-5 py-2.5 rounded hover:bg-nkf-navy">Search</button>
        </div>

        <div className="text-sm text-slate-500 mb-3">{loading ? "Loading…" : `${total} registered ${role}${total === 1 ? "" : "s"}`}</div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="federation-card bg-white border border-slate-200 rounded-md p-5">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-nkf-blue/10 border border-nkf-blue/20 flex items-center justify-center text-nkf-blue font-display text-lg">
                  {(it.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-display text-base text-nkf-blue truncate">{it.name}</div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">{it.membership_no || "—"}</div>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-sm text-slate-600">
                <div className="flex items-center gap-2"><User2 className="h-3.5 w-3.5 text-nkf-red" /> {role.charAt(0).toUpperCase() + role.slice(1)}</div>
                <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-nkf-red" /> {it.state || "—"}{it.district ? `, ${it.district}` : ""}</div>
              </div>
            </div>
          ))}
          {!loading && !items.length && (
            <div className="col-span-full p-8 text-center text-slate-500 border border-dashed rounded-md">No registered members found for these filters.</div>
          )}
        </div>
      </section>
    </div>
  );
}
